import { Dialog } from '@mui/material';
import { navigate } from 'gatsby';
import type { MUIDataTableOptions } from 'mui-datatables';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import Attachment from '@components/Attachments/Attachment';
import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import MuiDataTableButton from '@components/DataTable/MuiDataTable/TableButton';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import * as Questionnaire from '@components/Questionnaire';

import { ASSESSMENTS_DHA } from '@constants/routes';

import { useGetAllPatientUsers } from '@hooks/api/user';
import { useCuration } from '@hooks/useCuration';
import { useIsMobile } from '@hooks/useIsMobile';

import { DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER } from '@models/questionnaire';
import { RiskAssessmentRecord } from '@models/risk-assessment';

import { formatDate, formatName } from '@utils/format';

import { AssessmentType } from 'generated/types';

import { Breakpoint } from '../../../theme/breakpoints';
import PatientMobileRow from './MobileRow';
import PatientDialog from './PatientDialog';
import type { RowData } from './types';

const messages = defineMessages({
  name: {
    id: 'GLOBAL.name',
    defaultMessage: 'Name',
  },
  dob: {
    id: 'GLOBAL.dob',
    defaultMessage: 'Date of Birth',
  },
  consentQrCode: {
    id: 'assessment.consentQrCode',
    defaultMessage: 'Consent/QR Code',
  },
  completionDate: {
    id: 'assessment.completionDate',
    defaultMessage: 'Completion Date',
  },
  pdfReport: {
    id: 'assessment.pdfReport',
    defaultMessage: 'PDF Report',
  },
  noPatients: {
    id: 'assessment.noPatients',
    defaultMessage: 'No Patients',
  },
  patients: {
    id: 'assessment.patients',
    defaultMessage: 'Patients',
  },
  startAssessment: {
    id: 'assessment.startAssessment',
    defaultMessage: 'Start Assessment',
  },
  viewAssessment: {
    id: 'assessment.viewAssessment',
    defaultMessage: 'View Latest Assessment',
  },
  questionnaire: {
    id: 'assessment.questionnaire',
    defaultMessage: 'Questionnaire',
  },
  assignedUser: {
    id: 'assessment.assignedUser',
    defaultMessage: 'Assigned User',
  },
});

function PatientList() {
  const { formatMessage } = useIntl();
  const { viewAsPatient, reset } = useCuration();
  const isMobile = useIsMobile('md');

  const [isAssessmentOpen, setIsAssessmentOpen] = useState(false);
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] =
    useState<RowData['vitall_patient']>();

  const { data: patientsData = [], loading: patientsLoading } =
    useGetAllPatientUsers({ assessmentType: AssessmentType.BHA });
  const loading = patientsLoading;

  React.useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patientData = patientsData.map<RowData>((a) => {
    const { first_name, last_name, user_id, vitall_patient, plan } = a;
    const latestAssessment = vitall_patient?.latest_assessment;
    const riskAssessment = latestAssessment?.risk_assessment
      ? new RiskAssessmentRecord(latestAssessment.risk_assessment)
      : null;
    return {
      name: `${last_name}, ${first_name}`,
      vitall_patient: {
        first_name,
        last_name,
        vitall_patient_id: vitall_patient?.vitall_patient_id ?? '',
        patient_id: vitall_patient?.patient_id ?? '',
        //Based on patient plan DHA credit availability
        disabled: plan?.credits.find((c) => c.id === 'dha')?.remaining === 0,
      },
      assigned_user: formatName(
        a.created_user?.first_name,
        a.created_user?.last_name
      ),
      user_id,
      dob: vitall_patient?.date_of_birth ?? '',
      assessmentDate: riskAssessment?.assessmentDate,
      assessment_id: latestAssessment?.assessment_id,
      risk_assessment: riskAssessment
        ? {
            assessment: riskAssessment,
            patient_id: vitall_patient?.patient_id ?? '',
          }
        : undefined,
    };
  });

  const handleStartAssessment = (vitall_patient: RowData['vitall_patient']) => {
    setIsAssessmentOpen(true);
    setSelectedPatient(vitall_patient);
  };

  const handleCloseAssessment = () => {
    setSelectedPatient(undefined);
    setIsAssessmentOpen(false);
  };

  const handleStartQuestionnaire = (
    vitall_patient: RowData['vitall_patient']
  ) => {
    setIsQuestionnaireOpen(true);
    setSelectedPatient(vitall_patient);
  };

  const handleCloseQuestionnaire = () => {
    setSelectedPatient(undefined);
    setIsQuestionnaireOpen(false);
  };

  const handleRowClick: MUIDataTableOptions['onRowClick'] = async (
    _rowData,
    { dataIndex }
  ) => {
    const row = patientData[dataIndex];
    const { assessment_id, user_id } = row;
    if (assessment_id) {
      await viewAsPatient(user_id);
      navigate(ASSESSMENTS_DHA);
    }
  };

  return (
    <ContentLoader loading={loading}>
      {selectedPatient && (
        <Questionnaire.Dialog
          isOpen={isQuestionnaireOpen}
          onClose={handleCloseQuestionnaire}
          identifier={DEMOGRAPHICS_QUESTIONNAIRE_IDENTIFIER}
          name={formatName(
            selectedPatient.first_name,
            selectedPatient.last_name
          )}
          patient_id={selectedPatient.patient_id}
        />
      )}

      <MuiDataTable
        columns={[
          {
            name: 'name',
            className: 'blue-hover',
            label: formatMessage(messages.name),
            options: {
              searchable: true,
              sort: true,
            },
          },
          {
            name: 'dob',
            className: 'date',
            label: formatMessage(messages.dob),
            options: {
              sort: true,
              customBodyRender: (dob?: string | null) =>
                dob ? formatDate({ date: dob }) : '-',
            },
          },
          {
            name: 'assigned_user',
            className: 'small',
            label: formatMessage(messages.assignedUser),
            options: {
              sort: true,
            },
          },
          {
            name: 'assessmentDate',
            className: 'date',
            label: formatMessage(messages.completionDate),
            options: {
              filter: true,
              sort: true,
              customBodyRender: (lastLogin?: string | null) =>
                lastLogin
                  ? formatDate({ date: lastLogin, formatter: 'datetime' })
                  : '-',
            },
            desktopOnly: true,
          },
          {
            name: 'risk_assessment',
            label: formatMessage(messages.pdfReport),
            options: {
              customBodyRender: (value: RowData['risk_assessment']) => {
                const attachment = value?.assessment?.attachments.at(0);
                if (!value || !value.assessment || !attachment) {
                  return '-';
                }
                return (
                  <Attachment
                    patient_id={value.patient_id}
                    attachment={attachment}
                    resource={value.assessment.getResourceId()}
                  />
                );
              },
            },
          },
          {
            name: 'vitall_patient',
            className: 'small',
            label: ' ',
            options: {
              customBodyRender: (value: RowData['vitall_patient']) => (
                <MuiDataTableButton
                  // Disable button after BHA completion
                  disabled={value.disabled}
                  onClick={() => handleStartAssessment(value)}
                  text={formatMessage(messages.startAssessment)}
                />
              ),
            },
          },
          {
            name: 'risk_assessment',
            options: {
              customHeadLabelRender: () => null,
              customBodyRender: (value: RowData['risk_assessment']) => {
                const id = value?.assessment?.id;
                if (!id) {
                  return '';
                }
                return (
                  <MuiDataTableButton
                    text={formatMessage(messages.viewAssessment)}
                  />
                );
              },
            },
          },
          {
            name: 'vitall_patient',
            options: {
              customHeadLabelRender: () => null,
              customBodyRender: (value: RowData['vitall_patient']) => {
                return (
                  <MuiDataTableButton
                    onClick={() => handleStartQuestionnaire(value)}
                    text={formatMessage(messages.questionnaire)}
                  />
                );
              },
            },
          },
        ]}
        data={patientData}
        options={{
          search: true,
          sort: true,
          filter: true,
          onRowClick: handleRowClick,
          customRowRender: isMobile
            ? (data, dataIndex, rowIndex) => {
                const rowData = patientData[dataIndex];
                return (
                  <PatientMobileRow
                    key={rowData.user_id}
                    data={rowData}
                    dataIndex={dataIndex}
                    onRowClick={() =>
                      handleRowClick(data, { dataIndex, rowIndex })
                    }
                    onStartAssessment={() =>
                      handleStartAssessment(rowData.vitall_patient)
                    }
                    onStartQuestionnaire={() =>
                      handleStartQuestionnaire(rowData.vitall_patient)
                    }
                  />
                );
              }
            : undefined,
          sortOrder: {
            name: 'name',
            direction: 'asc',
          },
          textLabels: {
            body: {
              noMatch: formatMessage(messages.noPatients),
            },
          },
        }}
        title={formatMessage(messages.patients)}
      />
      <Dialog
        onClose={handleCloseAssessment}
        open={isAssessmentOpen}
        maxWidth="sm"
        fullWidth
        sx={{ maxHeight: Breakpoint.SM }}
        PaperProps={{
          sx: {
            minWidth: 'inherit',
            height: 'fit-content',
            // maxHeight: 450,
            maxWidth: 450,
          },
        }}
      >
        {selectedPatient?.patient_id && (
          <PatientDialog
            name={formatName(
              selectedPatient.first_name,
              selectedPatient.last_name
            )}
            onClose={handleCloseAssessment}
            patient_id={selectedPatient.patient_id}
            vitall_patient_id={selectedPatient.vitall_patient_id}
          />
        )}
      </Dialog>
    </ContentLoader>
  );
}

export default withErrorBoundary(PatientList, {
  FallbackComponent: ErrorBoundaryFallback,
});
